import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCart, addToCart, updateCartItem, removeFromCart, clearCart } from '../api';
import { showCartToast, pulseCartIcon, showCartAddedToast } from './CartUiContext';
import { getApiOrigin } from '../utils/apiBase';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const updateTimeoutRef = useRef({});

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCart();
      setCartItems(
        (data || []).map((item) => ({
          id: item.product_id,
          cartItemId: item.id,
          name: item.product_name,
          price: item.unit_price,
          quantity: item.quantity,
          image: item.product_image?.startsWith('http')
            ? item.product_image
            : item.product_image
              ? `${getApiOrigin()}${item.product_image.startsWith('/') ? '' : '/'}${item.product_image}`
              : null,
          ivaPercent: Number(item.iva_percent ?? 16),
          freeShipping: Boolean(item.free_shipping),
          shippingCost: Number(item.shipping_cost ?? 0),
        }))
      );
    } catch {
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCartHandler = async (product, quantity = 1) => {
    const productId = Number(product?.id);
    const qty = Math.max(1, Number(quantity) || 1);
    if (!productId) {
      throw new Error('Producto inválido');
    }
    try {
      await addToCart(productId, qty);
      // Actualización optimista inmediata (el catálogo no espera el GET)
      setCartItems((prev) => {
        const existing = prev.find((i) => i.id === productId);
        if (existing) {
          return prev.map((i) =>
            i.id === productId ? { ...i, quantity: (Number(i.quantity) || 0) + qty } : i,
          );
        }
        return [
          ...prev,
          {
            id: productId,
            cartItemId: null,
            name: product?.name || product?.nombre || '',
            price: Number(product?.price) || 0,
            quantity: qty,
            image: product?.image || null,
            ivaPercent: Number(product?.iva_percent ?? product?.ivaPercent ?? 16),
            freeShipping: Boolean(product?.free_shipping ?? product?.freeShipping),
            shippingCost: Number(product?.shipping_cost ?? product?.shippingCost ?? 0),
          },
        ];
      });
      pulseCartIcon('add');
      showCartAddedToast(product?.name || product?.nombre);
      try {
        await fetchCart();
      } catch (refreshErr) {
        // El producto ya se agregó; no fallar la UI por un refresh
        console.warn('Carrito agregado, pero falló el refresh:', refreshErr);
      }
    } catch (err) {
      console.error('Error al añadir al carrito:', err);
      try {
        await fetchCart();
      } catch {
        /* ignore */
      }
      throw err;
    }
  };

  const removeFromCartHandler = async (productId, { pulse = true } = {}) => {
    const item = cartItems.find((i) => i.id === productId);
    if (!item?.cartItemId) {
      return;
    }

    if (pulse) pulseCartIcon('remove');

    // Actualización optimista: eliminar del estado local inmediatamente
    const previousCartItems = [...cartItems];
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));

    try {
      await removeFromCart(item.cartItemId);
    } catch (err) {
      // Revertir cambios en caso de error
      setCartItems(previousCartItems);
      console.error('Error al eliminar del carrito:', err);
      
      showCartToast('No se pudo eliminar el producto. Intentá de nuevo.');
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      pulseCartIcon('remove');
      await removeFromCartHandler(productId, { pulse: false });
      return;
    }
    const item = cartItems.find((i) => i.id === productId);

    // Si el ítem aún no tiene cartItemId (actualización optimista), sumar vía POST
    if (!item?.cartItemId) {
      const prevQty = Number(item?.quantity) || 0;
      const delta = quantity - prevQty;
      if (delta > 0) {
        await addToCart(productId, delta);
        pulseCartIcon('add');
        setCartItems((prev) => {
          const existing = prev.find((i) => i.id === productId);
          if (existing) {
            return prev.map((i) =>
              i.id === productId ? { ...i, quantity } : i,
            );
          }
          return prev;
        });
        await fetchCart();
      } else {
        await fetchCart();
      }
      return;
    }

    const prevQty = Number(item.quantity) || 0;
    if (quantity > prevQty) pulseCartIcon('add');
    else if (quantity < prevQty) pulseCartIcon('remove');
    setCartItems((prevItems) =>
      prevItems.map((row) =>
        row.id === productId ? { ...row, quantity } : row,
      ),
    );

    // Cancelar timeout anterior si existe
    if (updateTimeoutRef.current[productId]) {
      clearTimeout(updateTimeoutRef.current[productId]);
    }

    // Debounce: esperar 500ms antes de enviar al servidor
    updateTimeoutRef.current[productId] = setTimeout(async () => {
      try {
        await updateCartItem(item.cartItemId, quantity);
      } catch (err) {
        // En caso de error, recargar el carrito para obtener el estado correcto
        await fetchCart();

        const msg = err.response?.data?.detail || 'No se pudo actualizar la cantidad';
        showCartToast(typeof msg === 'string' ? msg : 'Stock insuficiente');
      }
      delete updateTimeoutRef.current[productId];
    }, 500);
  };

  const clearCartHandler = async () => {
    try {
      await clearCart();
      setCartItems([]);
    } catch (err) {
      console.error('Error al vaciar carrito:', err);
    }
  };

  const getTotalItems = () => cartItems.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  /** Subtotal sin IVA (precio unitario del backend × cantidad). */
  const getTotalPrice = () => cartItems.reduce((total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);

  const getCartTotals = useCallback(() => {
    let subtotalExIva = 0;
    let ivaTotal = 0;
    let shippingTotal = 0;
    for (const item of cartItems) {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      const line = price * quantity;
      subtotalExIva += line;
      const pct = Number(item.ivaPercent ?? 16) / 100;
      ivaTotal += line * pct;
      if (!item.freeShipping) {
        shippingTotal += (Number(item.shippingCost) || 0) * quantity;
      }
    }
    return {
      subtotalExIva,
      ivaTotal,
      shippingTotal,
      grandTotal: subtotalExIva + ivaTotal + shippingTotal,
    };
  }, [cartItems]);

  const value = {
    cartItems,
    loading,
    addToCart: addToCartHandler,
    removeFromCart: removeFromCartHandler,
    updateQuantity,
    clearCart: clearCartHandler,
    getTotalItems,
    getTotalPrice,
    getCartTotals,
    refreshCart: fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
